import { useEffect, useRef } from 'react';
import { useLocalization } from '@canvas-logic/clp-shared-ui';
import { isLanguageUnavailableError } from '@canvas-logic/clp-localization';

import type { LanguageIssue } from './languageFallback';

export interface LanguageDebugContext {
  projectId: number | undefined;
  catalogId: number | undefined;
  productId: number | undefined;
  draft: boolean | undefined;
}

interface Props {
  requestedLanguage: string | undefined;
  debugContext: LanguageDebugContext;
  onLanguageUnavailable: (issue: LanguageIssue) => void;
}

/**
 * Reports unavailable `?lang=` diagnostics. A platform rejection is sent to
 * the owner of the `language` prop, which is the only place that can re-issue
 * the request. A successful platform fallback is logged to the console but
 * does not trigger UI recovery or a notification.
 *
 * Error responses are discriminated by platform code, not status — a
 * malformed include/layers list is also a 400 and must stay a visible bug.
 */
export function LanguageFallbackGuard({ requestedLanguage, debugContext, onLanguageUnavailable }: Props): null {
  const { status, error, resolvedLanguage, defaultLanguage } = useLocalization();
  const { projectId, catalogId, productId, draft } = debugContext;
  const route = draft || catalogId == null ? 'live' : 'active';
  // Keyed by language: a second bad ?lang= is worth reporting, a re-render isn't.
  const reportedFor = useRef<string | null>(null);

  useEffect(() => {
    if (
      status === 'ready' &&
      requestedLanguage != null &&
      resolvedLanguage != null &&
      requestedLanguage.toLowerCase() !== resolvedLanguage.toLowerCase()
    ) {
      // When the language prop changes, the provider may render once with the
      // previous ready payload before its request effect switches to loading.
      // Defer diagnostics so that transient render can cancel this callback.
      const timeoutId = window.setTimeout(() => {
        if (reportedFor.current === requestedLanguage) return;
        reportedFor.current = requestedLanguage;

        console.error(
          `[localization] Requested language "${requestedLanguage}" is not available; ` +
            `platform resolved "${resolvedLanguage}".`,
          {
            requestedLanguage,
            resolvedLanguage,
            defaultLanguage,
            projectId,
            catalogId,
            productId,
            draft,
            route,
          },
        );
      }, 0);
      return () => window.clearTimeout(timeoutId);
    }

    if (status !== 'error' || !isLanguageUnavailableError(error) || requestedLanguage == null) return;
    if (reportedFor.current === requestedLanguage) return;
    reportedFor.current = requestedLanguage;

    // Nothing resolved yet = initial load, so there is no working language to keep.
    const fallbackApplied = resolvedLanguage == null;

    console.error(
      `[localization] Requested language "${requestedLanguage}" is not available; ` +
        (fallbackApplied ? 'falling back to the project default.' : `keeping "${resolvedLanguage}".`),
      {
        requestedLanguage,
        resolvedLanguage,
        fallbackApplied,
        appErrorCode: error.appErrorCode,
        status: error.status,
        serverMessage: error.message,
        projectId,
        catalogId,
        productId,
        draft,
        // `live` → add the language to the project; `active` → re-release the catalog.
        route,
      },
    );

    onLanguageUnavailable({ requested: requestedLanguage, fallbackApplied });
  }, [
    status,
    error,
    resolvedLanguage,
    defaultLanguage,
    requestedLanguage,
    projectId,
    catalogId,
    productId,
    draft,
    route,
    onLanguageUnavailable,
  ]);

  return null;
}
